import { decode as bhDecode } from 'blurhash';

// Infinite-scroll batch size. Full result sets are unbounded (whole catalogue
// ≈ 700) — batching keeps each append a small DOM/network burst and lazy
// images bound the decoded memory to roughly the viewport's surroundings.
const BATCH = 48;

export default class SearchRenderer {
    destroy() {
        this._observer?.disconnect();
        this._observer = null;
        this._state = null;
        this._sentinel = null;
        this._nodes = null;
    }

    // Keyed render: cards are reused by item id across renders, so typing a
    // query refines the grid without recreating <img> elements — previously a
    // full innerHTML wipe re-decoded up to 120 images on every debounce tick
    // (visible flash while typing; decode churn on phones).
    //
    // Pagination: the first BATCH renders immediately; a sentinel div at the
    // grid's end appends the next BATCH when it comes within ~900px of the
    // viewport, until the result set is exhausted.
    render(results, container, debug = false) {
        this._observer?.disconnect();
        this._observer = null;

        if (!results.length) {
            container.replaceChildren();
            container.innerHTML = '<div class="col-span-full text-sm opacity-50">No results found.</div>';
            return;
        }

        // Persistent card cache — survives across renders so clearing a query
        // restores the initial set without rebuilding it. Bounded well above
        // the full catalogue (~700); reset wholesale if it somehow overflows.
        if (!this._nodes || this._nodes.size > 1000) this._nodes = new Map();

        this._state = { results, debug, shown: 0, container };
        container.replaceChildren();
        this._appendBatch();

        if (this._state.shown < results.length) {
            const sentinel = document.createElement('div');
            sentinel.className = 'col-span-full h-px';
            container.appendChild(sentinel);
            this._sentinel = sentinel;
            this._observer = new IntersectionObserver(entries => {
                if (entries.some(e => e.isIntersecting)) this._appendBatch();
            }, { rootMargin: '900px' });
            this._observer.observe(sentinel);
        }
    }

    _appendBatch() {
        const st = this._state;
        if (!st) return;
        const dbg  = String(st.debug);
        const end  = Math.min(st.shown + BATCH, st.results.length);
        const frag = document.createDocumentFragment();
        for (const item of st.results.slice(st.shown, end)) {
            let el = this._nodes.get(item.id);
            if (!el || el.dataset.debug !== dbg) {
                el = this.buildCard(item, st.debug);
                this._nodes.set(item.id, el);
            }
            frag.appendChild(el);
        }
        st.shown = end;
        // Insert before the sentinel so it stays last; null target = append.
        st.container.insertBefore(frag, this._sentinel?.parentNode === st.container ? this._sentinel : null);

        if (st.shown >= st.results.length) {
            this._observer?.disconnect();
            this._observer = null;
            this._sentinel?.remove();
            this._sentinel = null;
        }
    }

    buildCard(item, debug) {
        const title    = item.title || 'Untitled';
        const type     = item.type  || '';
        const keywords = item.keywords || '';
        const imageUrl      = this.thumbUrl(item);
        const isAccessory   = type === 'accessory';
        const frameClass    = isAccessory ? 'bg-white' : 'bg-neutral-900';
        const imgFitClass   = isAccessory ? 'object-contain' : 'object-cover';
        const editUrl  = `${window.JK.pbUrl}/_/#/collections/${item.collectionId}/records/${item.id}`;
        const slug     = (item.title || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        const wallLink = (type === 'look')
            ? `${window.JK.baseUrl}/collections?item=${slug}`
            : (type === 'accessory')
            ? `${window.JK.baseUrl}/accessories?item=${slug}`
            : (type === 'press' || type === 'invite' || type === 'campaign')
                ? `${window.JK.baseUrl}/wall?item=${slug}`
                : (type === 'video')
                    ? `${window.JK.baseUrl}/videos?item=${item.id}`
                    : null;

        // Blurhash average tint behind the image while it loads (accessories
        // keep their white frame) — same treatment as the galleries.
        const tint = !isAccessory && item.img_blurhash ? this.bhTint(item) : '';

        const el = document.createElement('div');
        el.className = 'search-result';
        el.dataset.id    = item.id;
        el.dataset.debug = String(debug);
        if (wallLink) el.dataset.wallLink = wallLink;
        el.style.cursor = wallLink ? 'pointer' : 'default';
        el.innerHTML = `
            ${imageUrl
                ? `<div class="search-result-image aspect-[2/3] ${frameClass} overflow-hidden"${tint ? ` style="background-color:${tint}"` : ''}>
                       <img src="${imageUrl}" alt="${this.escape(title)}" loading="lazy" decoding="async" width="400" height="600" class="w-full h-full ${imgFitClass}" />
                   </div>`
                : `<div class="search-result-image aspect-[2/3] ${frameClass}"></div>`
            }
            <div class="search-result-content pt-2">
                <p class="text-sm uppercase">${this.escape(title)}</p>
                ${debug && keywords
                    ? `<p class="search-keywords text-xs opacity-30 mt-1">${this.escape(keywords)}</p>`
                    : ''
                }
                ${debug
                    ? `<a href="${editUrl}" target="_blank" class="text-xs opacity-40 underline mt-1 inline-block">Edit in PocketBase</a>`
                    : ''
                }
            </div>
        `;
        return el;
    }

    bhTint(item) {
        if (item._bhTint === undefined) {
            try {
                const px = bhDecode(item.img_blurhash, 1, 1);
                item._bhTint = `rgb(${px[0]},${px[1]},${px[2]})`;
            } catch { item._bhTint = ''; }
        }
        return item._bhTint;
    }

    thumbUrl(item) {
        // The grid is 2-col on phones (~165px cells) and 4–6-col on desktop.
        // 800px (often 1MB+ for tall press/look images, then cropped to 2:3) is
        // pure waste on a phone — use 300px there, keep 800px for retina desktop.
        const size = window.innerWidth < 768 ? '300x0' : '800x0';
        if (item.type === 'video') {
            if (!item.thumbnail || !item.collectionId || !item.id) return null;
            return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.thumbnail}?thumb=${size}`;
        }
        if (!item.file || !item.collectionId || !item.id) return null;
        return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.file}?thumb=${size}`;
    }

    escape(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
}
